import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useApprovalQueue } from '../hooks/useApprovalQueue';
import { useOverviewMetrics } from '../hooks/useMetrics';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: string;
  badge?: number;
  badgeVariant?: 'red' | 'blue' | 'gray';
}

function NavBadge({ count, variant }: { count: number; variant: 'red' | 'blue' | 'gray' }) {
  const colors = {
    red: { bg: '#dc2626', text: '#fff' },
    blue: { bg: '#2563eb', text: '#fff' },
    gray: { bg: '#6b7280', text: '#fff' },
  };
  const c = colors[variant];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '20px',
        height: '20px',
        padding: '0 5px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 700,
        backgroundColor: c.bg,
        color: c.text,
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { data: queue } = useApprovalQueue();
  const { data: metrics } = useOverviewMetrics();

  const pendingCount = queue?.length ?? 0;
  const unreadReplies = metrics?.positiveReplies ?? 0;
  const highIntent = metrics?.highIntent ?? 0;

  const navItems: NavItem[] = [
    { to: '/', label: 'Overview', icon: '◈' },
    {
      to: '/prospects',
      label: 'Prospects',
      icon: '◉',
      badge: metrics?.awaitingApproval && metrics.awaitingApproval > 0
        ? metrics.awaitingApproval
        : undefined,
      badgeVariant: 'gray',
    },
    {
      to: '/approval',
      label: 'Approval Queue',
      icon: '✓',
      badge: pendingCount > 0 ? pendingCount : undefined,
      badgeVariant: 'red',
    },
    {
      to: '/replies',
      label: 'Replies',
      icon: '↩',
      badge: unreadReplies > 0 ? unreadReplies : undefined,
      badgeVariant: 'blue',
    },
    {
      to: '/opportunities',
      label: 'Opportunities',
      icon: '🔥',
      badge: highIntent > 0 ? highIntent : undefined,
      badgeVariant: 'blue',
    },
    { to: '/suppression', label: 'Suppression', icon: '⊘' },
    { to: '/settings', label: 'Settings', icon: '⚙' },
  ];

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Sidebar */}
      <nav
        style={{
          width: '228px',
          minWidth: '228px',
          backgroundColor: '#0f172a',
          color: '#cbd5e1',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Brand */}
        <div
          style={{
            padding: '24px 20px 20px',
            borderBottom: '1px solid #1e293b',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
              }}
            >
              ⚡
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#f1f5f9' }}>
                Prospecting
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>Agent Control</div>
            </div>
          </div>
        </div>

        {/* Nav Links */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
          {navItems.map((item) => {
            const isActive =
              item.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.to);

            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 12px',
                  borderRadius: '7px',
                  marginBottom: '2px',
                  textDecoration: 'none',
                  color: isActive ? '#f1f5f9' : '#94a3b8',
                  backgroundColor: isActive ? '#1e293b' : 'transparent',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '14px',
                  transition: 'all 0.1s',
                }}
              >
                <span style={{ width: '18px', textAlign: 'center', fontSize: '15px' }}>
                  {item.icon}
                </span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.badge !== undefined && (
                  <NavBadge count={item.badge} variant={item.badgeVariant ?? 'gray'} />
                )}
              </NavLink>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '14px 16px',
            borderTop: '1px solid #1e293b',
            fontSize: '12px',
            color: '#475569',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#22c55e',
              }}
            />
            <span>
              {import.meta.env.VITE_ENV ?? 'production'}
            </span>
          </div>
          <div style={{ marginTop: '4px', color: '#334155' }}>
            v{import.meta.env.VITE_VERSION ?? '0.1.0'}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: '#0f172a',
          color: '#e2e8f0',
        }}
      >
        {children}
      </main>
    </div>
  );
}
